import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { NoteBody } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import type { EditorMode } from '@backrs_ui/grow-ui/src/redux/coachNoteEditor/coachNoteEditor';

import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';

import {
  buildNotePreview,
  extractPlaintextFromBody,
  formatNoteTimestamp,
} from './coachNotes.utils';

const strings = appStrings.modules.coachingDashboard.coachNotes;

type MinimizedNoteProps = Readonly<{
  sessionId: string;
  mode: EditorMode;
  createdAt: string | null;
  body: NoteBody | null;
  onRestore: () => void;
  onClose: () => void;
}>;

export const MinimizedNote = ({
  sessionId,
  mode,
  createdAt,
  body,
  onRestore,
  onClose,
}: MinimizedNoteProps) => {
  const preview = body ? buildNotePreview(extractPlaintextFromBody(body)) : '';
  const title =
    preview || (mode === 'create' ? strings.editorTitleCreate : strings.editorTitleEdit);
  const subtitle = createdAt ? formatNoteTimestamp(createdAt) : '';

  return (
    <Box
      id={`coach-note-minimized-restore-${sessionId}`}
      role="button"
      tabIndex={0}
      onClick={onRestore}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onRestore();
      }}
      sx={containerSx}
    >
      <Box sx={textGroupSx}>
        <Typography variant="text-sm-semibold" sx={titleSx}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="text-xs-regular" sx={subtitleSx}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <IconButton
        id={`coach-note-minimized-close-btn-${sessionId}`}
        aria-label={strings.minimizedCloseLabel}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        size="small"
        sx={closeBtnSx}
      >
        <AppIcon iconName="XClose" diameter="16px" color="Gray/600" />
      </IconButton>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
  width: '240px',
  paddingInline: backrs.spacing['spacing-lg'],
  paddingBlock: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-lg'],
  cursor: 'pointer',
});

const textGroupSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
};

const titleSx: SxProps<Theme> = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const closeBtnSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  padding: backrs.spacing['spacing-xxs'],
  marginTop: `-${backrs.spacing['spacing-xxs']}`,
  marginRight: `-${backrs.spacing['spacing-xs']}`,
});
